const chapter = {
  id: 'p-block-15',
  slug: 'p-block-15',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'p-Block — Group 15 (Nitrogen Family)',
        hindi: 'p-खण्ड — वर्ग 15 (नाइट्रोजन परिवार)',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'general',
      title: {
        english: 'General Characteristics — N, P, As, Sb, Bi',
        hindi: 'सामान्य विशेषताएँ - एन, पी, एएस, एसबी, बीआई',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Valence configuration $ns^2 np^3$ (half-filled, extra stable).',
        hindi: 'वैलेंस कॉन्फ़िगरेशन $ns^2 np^3$ (आधा भरा हुआ, अतिरिक्त स्थिर)।',
            },
            {
              english: 'Oxidation states: $-3, +3, +5$ (and intermediate $+1, +2, +4$ for N).',
        hindi: 'ऑक्सीकरण अवस्थाएँ: $-3, +3, +5$ (और N के लिए मध्यवर्ती $+1, +2, +4$)।',
            },
            {
              english: 'Down the group: metallic character $\\uparrow$ (N, P non-metal; As, Sb metalloid; Bi metal); $+5$ stability $\\downarrow$, $+3$ stability $\\uparrow$ (**inert pair effect**).',
        hindi: 'समूह में नीचे: धात्विक वर्ण $\\uparrow$ (N, P अधातु; As, Sb मेटालॉइड; द्विधातु); $+5$ स्थिरता $\\downarrow$, $+3$ स्थिरता $\\uparrow$ (**अक्रिय युग्म प्रभाव**)।',
            },
            {
              english: 'N is unique: small size, high EN, **no d-orbitals** (max covalency = 4); forms $p\\pi$–$p\\pi$ multiple bonds (N≡N).',
        hindi: 'एन अद्वितीय है: छोटा आकार, उच्च एन, **कोई डी-ऑर्बिटल्स नहीं** (अधिकतम सहसंयोजकता = 4); $p\\pi$–$p\\pi$ एकाधिक बांड (N≡N) बनाता है।',
            },
            {
              english: 'P, As, Sb, Bi exist as tetra-atomic $P_4$ etc.; N as diatomic $N_2$.',
        hindi: 'P, As, Sb, Bi टेट्रा-परमाणु $P_4$ आदि के रूप में मौजूद हैं; एन द्विपरमाणुक $N_2$ के रूप में।',
            },
            {
              english: 'Catenation: P > N (N–N is weak due to lone-pair repulsion).',
        hindi: 'श्रृंखलन: P > N (अकेला-जोड़ी प्रतिकर्षण के कारण N-N कमजोर है)। **लैब तैयारी**: ।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Property', 'N', 'P', 'As', 'Sb', 'Bi'],
          rows: [
            ['Atomic radius (pm)', '70', '110', '121', '141', '148'],
            ['IE₁ (kJ/mol)', '1402', '1012', '947', '834', '703'],
            ['EN (Pauling)', '3.0', '2.1', '2.0', '1.9', '1.9'],
            ['Common O.S.', '−3 to +5', '−3, +3, +5', '+3, +5', '+3, +5', '+3'],
          ],
        },
      ],
    },
    {
      id: 'dinitrogen',
      title: {
        english: 'Dinitrogen — N₂',
        hindi: 'डाइनाइट्रोजन — N₂',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Lab prep**: $NH_4Cl + NaNO_2 \\xrightarrow{\\Delta} N_2 + NaCl + 2H_2O$.',
        hindi: '**लैब तैयारी**: $NH_4Cl + NaNO_2 \\xrightarrow{\\Delta} N_2 + NaCl + 2H_2O$।',
            },
            {
              english: 'Very pure: thermal decomposition of $(NH_4)_2Cr_2O_7 \\to N_2 + Cr_2O_3 + 4H_2O$ or $Ba(N_3)_2 \\to Ba + 3N_2$.',
        hindi: 'बहुत शुद्ध: $(NH_4)_2Cr_2O_7 \\to N_2 + Cr_2O_3 + 4H_2O$ या $Ba(N_3)_2 \\to Ba + 3N_2$ का थर्मल अपघटन।',
            },
            {
              english: 'N≡N very strong (941 kJ/mol) ⇒ N₂ chemically inert at room T.',
        hindi: 'N≡N बहुत मजबूत (941 kJ/mol) ⇒ N₂ कक्ष T पर रासायनिक रूप से निष्क्रिय।',
            },
            {
              english: 'High-T: $N_2 + O_2 \\rightleftharpoons 2NO$ (in lightning / IC engines).',
        hindi: 'हाई-टी: $N_2 + O_2 \\rightleftharpoons 2NO$ (लाइटनिंग/आईसी इंजन में)।',
            },
            {
              english: 'Used in Haber process, inert atmosphere, liquid N₂ as cryogen.',
        hindi: 'हेबर प्रक्रिया, अक्रिय वातावरण, तरल N₂ में क्रायोजेन के रूप में उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'ammonia',
      title: {
        english: 'Ammonia — NH₃',
        hindi: 'अमोनिया — NH₃',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Haber process**: $N_2 + 3H_2 \\xrightleftharpoons{Fe/Mo, 200\\,atm, 700\\,K} 2NH_3$, $\\Delta H < 0$.',
        hindi: '**हैबर प्रक्रिया**: $N_2 + 3H_2 \\xrightleftharpoons{Fe/Mo, 200\\,atm, 700\\,K} 2NH_3$, $\\Delta H < 0$।',
            },
            {
              english: 'Lab prep: $2NH_4Cl + Ca(OH)_2 \\to 2NH_3 + CaCl_2 + 2H_2O$. Dried over **CaO** (not H₂SO₄ / P₄O₁₀ / CaCl₂).',
        hindi: 'लैब तैयारी: $2NH_4Cl + Ca(OH)_2 \\to 2NH_3 + CaCl_2 + 2H_2O$। **CaO** (H₂SO₄ / P₄O₁₀ / CaCl₂ नहीं) पर सुखाया गया।',
            },
            {
              english: 'Structure: pyramidal, $sp^3$, bond angle **107.8°** (lone-pair repulsion).',
        hindi: 'संरचना: पिरामिडनुमा, $sp^3$, बंधन कोण **107.8°** (अकेला-जोड़ी प्रतिकर्षण)।',
            },
            {
              english: 'Basic — forms $[Cu(NH_3)_4]^{2+}$ deep blue, $[Ag(NH_3)_2]^+$ (Tollens), $[Zn(NH_3)_4]^{2+}$.',
        hindi: 'मूल - फॉर्म $[Cu(NH_3)_4]^{2+}$ गहरा नीला, $[Ag(NH_3)_2]^+$ (टोलेंस), $[Zn(NH_3)_4]^{2+}$।',
            },
            {
              english: 'Uses: fertilisers (urea, $(NH_4)_2SO_4$), HNO₃ (Ostwald), refrigerant.',
        hindi: 'उपयोग: उर्वरक (यूरिया, $(NH_4)_2SO_4$), HNO₃ (ओस्टवाल्ड), रेफ्रिजरेंट। **ओस्टवाल्ड प्रक्रिया** (HNO₃ निर्माण):',
            },
          ],
        },
      ],
    },
    {
      id: 'oxoacids-n',
      title: {
        english: 'Oxides & Oxoacids of Nitrogen',
        hindi: 'नाइट्रोजन के ऑक्साइड और ऑक्सोअम्ल',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Oxide', 'O.S.', 'Nature', 'Notes'],
          rows: [
            ['$N_2O$', '+1', 'Neutral', 'Laughing gas; from $NH_4NO_3 \\xrightarrow{\\Delta}$'],
            ['$NO$', '+2', 'Neutral', 'Paramagnetic (odd e⁻)'],
            ['$N_2O_3$', '+3', 'Acidic', 'Anhydride of HNO₂'],
            ['$NO_2$', '+4', 'Acidic', 'Brown, paramagnetic; dimerises to $N_2O_4$'],
            ['$N_2O_5$', '+5', 'Acidic', 'Anhydride of HNO₃'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Ostwald process** (HNO₃ manufacture):',
        hindi: '**ओस्टवाल्ड प्रक्रिया** (HNO₃ निर्माण):',
            },
            {
              english: '$4NH_3 + 5O_2 \\xrightarrow{Pt/Rh, 500\\,K} 4NO + 6H_2O$;',
        hindi: '$4NH_3 + 5O_2 \\xrightarrow{Pt/Rh, 500\\,K} 4NO + 6H_2O$;',
            },
            {
              english: '$2NO + O_2 \\to 2NO_2$;',
        hindi: '$2NO + O_2 \\to 2NO_2$;',
            },
            {
              english: '$3NO_2 + H_2O \\to 2HNO_3 + NO$.',
        hindi: '$3NO_2 + H_2O \\to 2HNO_3 + NO$.',
            },
            {
              english: 'Concentrated $HNO_3$ ≈ 68 %; **fuming nitric acid** is HNO₃ + dissolved NO₂.',
        hindi: 'सांद्रित $HNO_3$ ≈ 68 %; **फ्यूमिंग नाइट्रिक एसिड** HNO₃ + घुली हुई NO₂ है।',
            },
            {
              english: 'Reactions of HNO₃: oxidising in all dilutions; with metals gives various N-oxides depending on concentration:',
        hindi: 'HNO₃ की प्रतिक्रियाएं: सभी तनुकरणों में ऑक्सीकरण; धातुओं के साथ सांद्रता के आधार पर विभिन्न एन-ऑक्साइड मिलते हैं:',
            },
            {
              english: '• Dil HNO₃ + Cu → NO; conc HNO₃ + Cu → NO₂.',
        hindi: '• दिल HNO₃ + Cu → NO; सांद्र HNO₃ + Cu → NO₂.',
            },
            {
              english: '• Dil HNO₃ + Mg/Mn → H₂ (only exception).',
        hindi: '• दिल HNO₃ + Mg/Mn → H₂ (केवल अपवाद)।',
            },
            {
              english: '• Au, Pt dissolve in **aqua regia** (3 HCl : 1 HNO₃) ⇒ $HAuCl_4$, $H_2PtCl_6$.',
        hindi: '• Au, Pt **एक्वा रेजिया** (3 HCl : 1 HNO₃) ⇒ $HAuCl_4$, $H_2PtCl_6$ में घुल जाता है।',
            },
            {
              english: 'Brown-ring test for NO₃⁻: $[Fe(H_2O)_5(NO)]^{2+}$.',
        hindi: 'NO₃⁻ के लिए ब्राउन-रिंग परीक्षण: $[Fe(H_2O)_5(NO)]^{2+}$।',
            },
          ],
        },
      ],
    },
    {
      id: 'phosphorus',
      title: {
        english: 'Phosphorus — Allotropes & Hydride',
        hindi: 'फॉस्फोरस — अपरूप और हाइड्राइड',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Allotropes: **white** P (P₄, tetrahedral, very reactive, glows in dark, stored under water), **red** (polymeric, less reactive), **black** (most stable).',
        hindi: 'एलोट्रोप्स: **सफेद** पी (पी₄, टेट्राहेड्रल, बहुत प्रतिक्रियाशील, अंधेरे में चमकता है, पानी के नीचे संग्रहीत), **लाल** (बहुलक, कम प्रतिक्रियाशील), **काला** (सबसे स्थिर)।',
            },
            {
              english: 'White → red on heating in inert atmosphere at 573 K.',
        hindi: '573 K पर अक्रिय वातावरण में गर्म करने पर सफेद → लाल।',
            },
            {
              english: '**PH₃ (phosphine)**: prep $Ca_3P_2 + 6H_2O \\to 2PH_3 + 3Ca(OH)_2$ or $P_4 + 3NaOH + 3H_2O \\to PH_3 + 3NaH_2PO_2$.',
        hindi: '**PH₃ (फॉस्फीन)**: तैयारी $Ca_3P_2 + 6H_2O \\to 2PH_3 + 3Ca(OH)_2$ या $P_4 + 3NaOH + 3H_2O \\to PH_3 + 3NaH_2PO_2$।',
            },
            {
              english: 'Pure PH₃ is non-spontaneous; ignites due to $P_2H_4$ impurity (Holme\'s signal in sea — Ca₃P₂ + water).',
        hindi: 'शुद्ध PH₃ गैर-सहज है; $P_2H_4$ अशुद्धता के कारण प्रज्वलित होता है (समुद्र में होल्मे का संकेत - Ca₃P₂ + पानी)।',
            },
            {
              english: 'Bond angle PH₃ = **93.5°** (less than NH₃ — large central atom, less lp-bp repulsion).',
        hindi: 'बॉन्ड कोण PH₃ = **93.5°** (NH₃ से कम - बड़ा केंद्रीय परमाणु, कम एलपी-बीपी प्रतिकर्षण)।',
            },
            {
              english: 'Basicity order: $NH_3 > PH_3 > AsH_3 > SbH_3 > BiH_3$.',
        hindi: 'मौलिकता क्रम: $NH_3 > PH_3 > AsH_3 > SbH_3 > BiH_3$.',
            },
          ],
        },
      ],
    },
    {
      id: 'phosphorus-halides-acids',
      title: {
        english: 'Halides & Oxoacids of Phosphorus',
        hindi: 'फॉस्फोरस के हैलाइड और ऑक्सोअम्ल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**PCl₃** ($sp^3$, pyramidal). Prep: $P_4 + 6Cl_2 \\to 4PCl_3$. Hydrolysis: $PCl_3 + 3H_2O \\to H_3PO_3 + 3HCl$.',
        hindi: '**PCl₃** ($sp^3$, पिरामिडनुमा)। तैयारी: $P_4 + 6Cl_2 \\to 4PCl_3$. हाइड्रोलिसिस: $PCl_3 + 3H_2O \\to H_3PO_3 + 3HCl$।',
            },
            {
              english: '**PCl₅** ($sp^3d$, **trigonal bipyramidal**; gas/liquid). In solid: $[PCl_4]^+ [PCl_6]^-$ (tetrahedral + octahedral).',
        hindi: '**PCl₅** ($sp^3d$, **त्रिकोणीय द्विपिरामिडल**; gas/liquid)। ठोस में: $[PCl_4]^+ [PCl_6]^-$ (चतुष्फलकीय + अष्टफलकीय)।',
            },
            {
              english: 'Hydrolysis: $PCl_5 + 4H_2O \\to H_3PO_4 + 5HCl$; partial: $PCl_5 + H_2O \\to POCl_3 + 2HCl$.',
        hindi: 'हाइड्रोलिसिस: $PCl_5 + 4H_2O \\to H_3PO_4 + 5HCl$; आंशिक: $PCl_5 + H_2O \\to POCl_3 + 2HCl$.',
            },
            {
              english: 'Axial bonds in PCl₅ are **longer** than equatorial (3 bp–bp repulsion in axial vs 2 in equatorial).',
        hindi: 'पीसीएल₅ में अक्षीय बंधन भूमध्यरेखीय से **लंबे** होते हैं (अक्षीय में 3 बीपी-बीपी प्रतिकर्षण बनाम भूमध्यरेखीय में 2)।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Acid', 'Formula', 'O.S. of P', 'Basicity'],
          rows: [
            ['Hypophosphorous', '$H_3PO_2$', '+1', '1 (one OH)'],
            ['Phosphorous', '$H_3PO_3$', '+3', '2 (two OH)'],
            ['Phosphoric (orthophosphoric)', '$H_3PO_4$', '+5', '3'],
            ['Pyrophosphoric', '$H_4P_2O_7$', '+5', '4'],
            ['Metaphosphoric', '$(HPO_3)_n$', '+5', 'n'],
          ],
        },
        {
          type: 'note',
          body: {
            english: '**Basicity = number of P–OH groups**, not total H. P–H bonds are **non-ionisable** but are **strong reducing** agents (e.g. H₃PO₂ reduces $AgNO_3$ to Ag).',
        hindi: '**Basicity = number of P–OH groups**, कुल H नहीं। P-H बांड **गैर-आयनीकरणीय** हैं, लेकिन **मजबूत कम करने वाले** एजेंट हैं (उदाहरण के लिए H₃PO₂ $AgNO_3$ को Ag तक कम कर देता है)।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'N_2 + 3H_2 \\xrightleftharpoons{Fe/Mo} 2NH_3',
      desc: {
        english: 'Haber',
        hindi: 'हैबर',
      },
    },
    {
      latex: '4NH_3 + 5O_2 \\xrightarrow{Pt/Rh} 4NO + 6H_2O',
      desc: {
        english: 'Ostwald step 1',
        hindi: 'ओस्टवाल्ड चरण 1',
      },
    },
    {
      latex: 'PCl_5 + 4H_2O \\to H_3PO_4 + 5HCl',
      desc: {
        english: 'PCl₅ hydrolysis',
        hindi: 'PCl₅ हाइड्रोलिसिस',
      },
    },
    {
      latex: 'P_4 + 3NaOH + 3H_2O \\to PH_3 + 3NaH_2PO_2',
      desc: {
        english: 'Phosphine prep',
        hindi: 'फॉस्फीन तैयारी',
      },
    },
    {
      latex: '\\angle HNH = 107.8^\\circ,\\ \\angle HPH = 93.5^\\circ',
      desc: {
        english: 'Bond angles',
        hindi: 'बॉन्ड कोण',
      },
    },
    {
      latex: 'NH_3 > PH_3 > AsH_3 > SbH_3 > BiH_3',
      desc: {
        english: 'Basicity',
        hindi: 'मौलिकता',
      },
    },
    {
      latex: '3HCl : 1HNO_3 = \\text{aqua regia}',
      desc: {
        english: 'Dissolves Au, Pt',
        hindi: 'एयू, पीटी को घोलता है',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Geometry',
      problem: {
        english: 'Hybridisation of P in PCl₅ (gas) is:',
        hindi: 'PCl₅ (गैस) में P का संकरण है:',
      },
      answer: {
        english: '$sp^3d$',
        hindi: '$sp^3d$',
      },
      solution: {
        english: 'Trigonal bipyramidal.',
        hindi: 'त्रिकोणीय द्विपिरामिडल।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Basicity',
      problem: {
        english: 'Basicity of $H_3PO_2$ is:',
        hindi: '$H_3PO_2$ की मौलिकता है:',
      },
      answer: {
        english: '1',
        hindi: '1',
      },
      solution: {
        english: 'Only one P–OH; two P–H non-ionisable.',
        hindi: 'केवल एक P–OH; दो पी-एच गैर-आयनीकरण योग्य।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Drying',
      problem: {
        english: 'NH₃ cannot be dried using:',
        hindi: 'NH₃ को इसका उपयोग करके नहीं सुखाया जा सकता:',
      },
      answer: {
        english: 'Conc. H₂SO₄ (acidic, reacts)',
        hindi: 'सांद्र। H₂SO₄ (अम्लीय, प्रतिक्रिया करता है)',
      },
      solution: {
        english: 'Use CaO instead.',
        hindi: 'इसके बजाय CaO का उपयोग करें।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Allotropes',
      problem: {
        english: 'Most reactive allotrope of phosphorus is:',
        hindi: 'फॉस्फोरस का सबसे प्रतिक्रियाशील अपरूप है:',
      },
      answer: {
        english: 'White (P₄)',
        hindi: 'सफ़ेद (P₄)',
      },
      solution: {
        english: 'Strained 60° angles in tetrahedron.',
        hindi: 'चतुष्फलक में 60° कोण तनावपूर्ण।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Oxoacid',
      problem: {
        english: 'Oxidation state of P in pyrophosphoric acid $H_4P_2O_7$:',
        hindi: 'पाइरोफॉस्फोरिक एसिड में P की ऑक्सीकरण अवस्था $H_4P_2O_7$:',
      },
      answer: {
        english: '+5',
        hindi: '+5',
      },
      solution: {
        english: '$4(+1) + 2x + 7(-2) = 0 \\Rightarrow x = +5$.',
        hindi: '$4(+1) + 2x + 7(-2) = 0 \\Rightarrow x = +5$।',
      },
    },
  ],
  next: {
    slug: 'p-block-16',
    title: 'p-Block — Group 16 (Oxygen Family)',
  },
};

export default chapter;
